import {
  ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { motion } from 'framer-motion'
import { DropdownOption } from '@/components/dropdown'
import { Button } from '@/components/ui/deprecated-button'
import { Input } from '@/components/ui/input'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/css-utils'
import { X } from 'lucide-react'
import { Alert, AlertDescription } from './ui/alert'
import { useDebouncedCallback } from 'use-debounce'

interface FullPageTypeaheadProps<T extends DropdownOption> {
  searchOptions: (input: string) => Promise<T[]> | T[]
  onSelect: (option: T) => void
  onClear: () => void
  name?: string
  defaultQuery?: string
  placeholder?: string
  debounceTimer?: number
  defaultOptions?: T[]
}

export default function FullPageTypeahead<T extends DropdownOption>({
  searchOptions,
  onSelect,
  onClear,
  placeholder,
  name,
  debounceTimer = 150,
  defaultQuery = '',
  defaultOptions,
}: FullPageTypeaheadProps<T>) {
  const [query, setQuery] = useState(defaultQuery)
  const [_results, setResults] = useState<T[] | undefined>([])
  const results = _results ?? []
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const [canShowNoResultsError, setCanShowNoResultsError] = useState(false)

  const debouncedSearchOptions = useDebouncedCallback(
    async (searchQuery: string) => {
      setLoading(true)
      const options = await searchOptions(searchQuery)
      setResults(options)
      setLoading(false)
    },
    debounceTimer,
  )

  useEffect(() => {
    if (!query) return
    debouncedSearchOptions(query)
    setActiveIndex(-1)
  }, [query, debouncedSearchOptions])

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1))
        break
      case 'Enter':
        if (activeIndex >= 0) {
          handleSelection(results[activeIndex])
        }
        break
      case 'Escape':
        setQuery('')
        setActiveIndex(-1)
        break
    }
  }

  useEffect(() => {
    if (activeIndex !== -1 && resultsRef.current) {
      const activeElement = resultsRef.current.children[
        activeIndex
      ] as HTMLElement
      activeElement.scrollIntoView({ block: 'center' })
    }
  }, [activeIndex])

  const handleSelection = (option: T) => {
    setCanShowNoResultsError(false)
    onSelect(option)
    setQuery('')
    setActiveIndex(-1)
  }
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setQuery(input)
    requestAnimationFrame(() => setCanShowNoResultsError(input.length > 0))
  }

  return (
    <div
      className="flex flex-col grow overflow-hidden bg-modal-bg"
      style={{ height: 'calc(100vh - 120px)' }}
    >
      <div className="relative">
        <Input
          ref={inputRef}
          value={query}
          name={name || 'search'}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="pr-8"
          placeholder={placeholder}
          aria-label={placeholder}
          aria-controls="search-results"
          autoComplete="off"
          aria-activedescendant={
            activeIndex >= 0 ? `result-${activeIndex}` : undefined
          }
        />
        {loading && (
          <div className="absolute right-2 top-2">
            <LoadingSpinner />
          </div>
        )}
        {query && !loading && (
          <Button
            variant="ghost"
            className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
            onClick={() => {
              setQuery('')
              onClear()
              inputRef.current?.focus()
            }}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {canShowNoResultsError &&
      !loading &&
      !results.length &&
      !defaultOptions?.length ? (
        <Alert variant="warning" className="mt-4">
          <AlertDescription>No results found</AlertDescription>
        </Alert>
      ) : null}

      <ScrollArea className="h-full grow">
        <div
          className="pt-4"
          ref={resultsRef}
          role="listbox"
          id="search-results"
          aria-labelledby="search-results-label"
        >
          {(results.length ? results : (defaultOptions ?? [])).map(
            (option, index) => (
              <motion.div
                key={option.id}
                role="option"
                id={`result-${index}`}
                aria-selected={index === activeIndex}
                className={cn(
                  'flex w-full flex-col px-4 py-3 text-left cursor-pointer text-copy-primary',
                  'hover:text-copy-accent focus:outline-1 focus:outline-input-focus-border',
                  { 'bg-select-item-focused-bg': index === activeIndex },
                )}
                onClick={() => handleSelection(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelection(option)
                  }
                }}
                tabIndex={-1}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                transition={{ duration: 0.15 }}
              >
                <span>{option.label}</span>
              </motion.div>
            ),
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
